import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { CompletionRecord, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  EmptyState,
  Field,
  InternalPanel,
  Spinner,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { formatDateTime, toDateTimeLocalValue } from '../lib/format.js';

export function CompletionSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor } = useSession();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mayManage =
    isContractor &&
    can(PERMISSIONS.COMPLETION_MANAGE) &&
    !['closed', 'cancelled'].includes(workOrder.status);

  const { data, isLoading } = useQuery({
    queryKey: ['completion', workOrder.id],
    queryFn: () =>
      apiRequest<{ completion: CompletionRecord | null }>(
        `/api/work-orders/${workOrder.id}/completion`,
      ).then((result) => result.completion),
  });

  const [form, setForm] = useState({
    completedAt: toDateTimeLocalValue(),
    workPerformed: '',
    finalNotes: '',
    finalNotesInternal: '',
    followUpRecommendation: '',
    exceptionsNoted: '',
    markCompleted: false,
    closeAfter: false,
  });

  useEffect(() => {
    if (!data) return;
    setForm((previous) => ({
      ...previous,
      completedAt: data.completedAt ? toDateTimeLocalValue(data.completedAt) : previous.completedAt,
      workPerformed: data.workPerformed ?? '',
      finalNotes: data.finalNotes ?? '',
      finalNotesInternal: data.finalNotesInternal ?? '',
      followUpRecommendation: data.followUpRecommendation ?? '',
      exceptionsNoted: data.exceptionsNoted ?? '',
    }));
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrder.id}/completion`, {
        method: 'PUT',
        body: {
          completedAt: new Date(form.completedAt).toISOString(),
          workPerformed: form.workPerformed.trim(),
          finalNotesCustomerVisible: form.finalNotes.trim() || undefined,
          finalNotesInternal: form.finalNotesInternal.trim() || undefined,
          followUpRecommendation: form.followUpRecommendation.trim() || undefined,
          exceptionsNoted: form.exceptionsNoted.trim() || undefined,
          markCompleted: form.markCompleted,
          closeAfter: form.markCompleted && form.closeAfter,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['completion', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The completion record could not be saved. Please try again.',
      );
    },
  });

  if (isLoading) return <Spinner label="Loading completion record" />;

  if (editing) {
    return (
      <Card title="Completion documentation">
        <div className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}
          <Alert variant="info">
            Completion photos are optional: attach them under Documents &amp; comments (Document
            type = Completion photo) when USPS asks for them.
          </Alert>

          <Field label="Date and time completed" htmlFor="completedAt" required>
            <TextInput
              id="completedAt"
              type="datetime-local"
              required
              max={toDateTimeLocalValue()}
              value={form.completedAt}
              onChange={(event) => setForm({ ...form, completedAt: event.target.value })}
            />
          </Field>

          <Field
            label="Work performed"
            htmlFor="workPerformed"
            required
            hint="What was actually done, parts replaced, and the result. Visible to USPS."
          >
            <TextArea
              id="workPerformed"
              rows={6}
              required
              minLength={10}
              value={form.workPerformed}
              onChange={(event) => setForm({ ...form, workPerformed: event.target.value })}
            />
          </Field>

          <Field label="Final notes for USPS" htmlFor="finalNotes">
            <TextArea
              id="finalNotes"
              rows={3}
              value={form.finalNotes}
              onChange={(event) => setForm({ ...form, finalNotes: event.target.value })}
            />
          </Field>

          <Field
            label="Follow-up recommendation"
            htmlFor="followUp"
            hint="Any further repair you recommend. Visible to USPS."
          >
            <TextArea
              id="followUp"
              rows={3}
              value={form.followUpRecommendation}
              onChange={(event) => setForm({ ...form, followUpRecommendation: event.target.value })}
            />
          </Field>

          <Field
            label="Exceptions noted"
            htmlFor="exceptions"
            hint="Anything in the approved scope that could not be completed. Visible to USPS."
          >
            <TextArea
              id="exceptions"
              rows={3}
              value={form.exceptionsNoted}
              onChange={(event) => setForm({ ...form, exceptionsNoted: event.target.value })}
            />
          </Field>

          <InternalPanel title="VGS internal completion notes">
            <TextArea
              aria-label="VGS internal completion notes"
              rows={3}
              value={form.finalNotesInternal}
              onChange={(event) => setForm({ ...form, finalNotesInternal: event.target.value })}
            />
          </InternalPanel>

          <label className="flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
              checked={form.markCompleted}
              onChange={(event) => setForm({ ...form, markCompleted: event.target.checked })}
            />
            {workOrder.kind === 'supplies'
              ? 'Mark the items as received and notify USPS'
              : 'Mark this work order complete and notify USPS'}
            {['approved', 'procurement', 'repair_scheduled'].includes(workOrder.status) && (
              <span className="block text-xs font-normal text-slate-600">
                The schedule and start are filled in from the completion date, so no separate
                Schedule or Start work step is needed.
              </span>
            )}
          </label>

          {form.markCompleted && (
            <label className="flex items-start gap-2 text-sm font-medium text-slate-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                checked={form.closeAfter}
                onChange={(event) => setForm({ ...form, closeAfter: event.target.checked })}
              />
              Also close the work order now
              <span className="block text-xs font-normal text-slate-600">
                Use this when nothing else is outstanding. Emergency work authorized against a
                not-to-exceed amount cannot close until USPS approves the final price; add the
                invoice document and VGS invoice number afterwards if you still need to.
              </span>
            </label>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              loading={save.isPending}
              disabled={form.workPerformed.trim().length < 10}
            >
              Save completion record
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Completion documentation"
      actions={
        mayManage && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {data ? 'Edit completion record' : 'Document completion'}
          </Button>
        )
      }
    >
      {!data ? (
        <EmptyState
          title="Not documented yet"
          description={
            isContractor
              ? 'Record what was performed when the repair is finished; attach completion photos if USPS asks for them.'
              : 'VGS will document the completed work here.'
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <DefinitionList
            items={[
              { label: 'Completed', value: formatDateTime(data.completedAt) },
              { label: 'Recorded by', value: data.completedByName },
            ]}
          />

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Work performed
            </h3>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{data.workPerformed}</p>
          </div>

          {data.finalNotes && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Final notes
              </h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{data.finalNotes}</p>
            </div>
          )}

          {data.followUpRecommendation && (
            <Alert variant="warning" title="Follow-up recommended">
              {data.followUpRecommendation}
            </Alert>
          )}

          {data.exceptionsNoted && (
            <Alert variant="warning" title="Exceptions noted">
              {data.exceptionsNoted}
            </Alert>
          )}

          {data.finalNotesInternal !== undefined && (
            <InternalPanel title="VGS internal completion notes">
              <p className="whitespace-pre-wrap text-sm text-slate-800">
                {data.finalNotesInternal ?? 'None recorded.'}
              </p>
            </InternalPanel>
          )}
        </div>
      )}
    </Card>
  );
}
